import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftOutline, CircledCheckIcon, MapPinIcon, PaymentCashIcon, PaymentLoyaltyIcon, PhoneIcon, SpinnerIcon, TrifoldMapIcon } from "../../../icons/";
import usePersonStore from "../../../store/personStore";
import { addToCalendar, formatBookingCalendar, validateResponse } from "../serviceUtils";
import { toast } from "../../../components/ui/Toast/useToast";
import { useOnlinePaymentInitit, useRedeemPaymentInit } from "../../../hooks/react-query/usePayment";
import React, { useEffect, useState } from "react";
import { getServiceBookingCancelFn, getServiceBookingShowSummaryFn } from "../../../transactions/serviceBooking/newServiceBooking";
import { queryClient } from "../../../utils/queryClient";
import { QUERY_KEYS } from "../../../config/constants";
import { ServiceSummarySkeletion } from "../../../components/elements/skeletions/skeletions";
import { ConfirmationDialog, CustomModal } from "../../../components/ui";
import { useServiceCarStore } from "../../../store/serviceCarStore";
import { getLoyaltyInfoFn } from "../../../transactions/loyalty/loyalty";

export const BookingSummary = ({ state, dispatch, afterViva, paymentStatus }) => {
  // Step five: Here the user sees a summary of his booked appointment

  // LOCAL STATE:
  const [isCRMForm, setIsCRMForm] = useState(true)
  const [isPickup, setIsPickup] = useState(false)
  const [isCancelLoading, setIsCancelLoading] = useState(false)
  const [isSummaryLoading, setIsSummaryLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [currentPaymentStatus, setCurrentPaymentStatus] = useState(paymentStatus);
  const loyaltyInfo = usePersonStore((store) => store.loyaltyInfo);

  // FETCH DATA:

  // LOCAL VARIABLES:
  const navigate = useNavigate();
  const { SERVICE_HISTORY } = QUERY_KEYS;
  const { flowId } = useParams();
  const { mutate: paymentInit } = useOnlinePaymentInitit();
  const { mutate: redeemInit, isLoading: isRedeemLoading } = useRedeemPaymentInit();
  const { carInfo } = useServiceCarStore();

  const resolvedFlowId = React.useMemo(
    () => flowId ?? state?.serviceFlowId,
    [flowId, state?.serviceFlowId]
  );

  const showRetailDashboard = usePersonStore(
    (store) => store.showRetailDashboard
  );

  // USE EFFECTS:

  // This useEffect is for determining the type of the appointment (CRM form or Pickup) based on the optionSelection, which will affect the displayed information and available actions in the summary page
  useEffect(()=> {
    if(["PickupCRMForm","CRMForm"].includes(state?.appointmentSummary?.optionSelection)) {
      setIsCRMForm(true)
    } else {
      setIsCRMForm(false)
    }

    if(["PickupCRMForm","PickupAvis"].includes(state?.appointmentSummary?.optionSelection)) {
      setIsPickup(true)
    } else {
      setIsPickup(false)
    }
  }, [state?.appointmentSummary])

  useEffect(() => {setCurrentPaymentStatus(paymentStatus);}, [paymentStatus]);
  
  // This useEffect is for showing a toast message after returning from Viva, based on the payment status
  useEffect(() => {
    if (!afterViva) return;

    if (currentPaymentStatus === 'success') {
      toast({
        variant: 'success',
        description: 'Η πληρωμή έγινε επιτυχώς',
      });
    } else if (currentPaymentStatus === 'fail') {
      toast({
        variant: 'destructive',
        description: 'Η πληρωμή σου απέτυχε.',
      });
    } else {
      toast({
        variant: 'destructive',
        description: 'Παρουσιάστηκε κάποιο πρόβλημα',
      });
    }
  }, [afterViva, currentPaymentStatus]);

  // HANDLERS & FUNCTIONS

  const vivaRepaymentHandler = () => {
    try {
      paymentInit(
        {
          serviceFlowId: resolvedFlowId,
          payment_type: 'pickupDelivery',
        },
        {
          onSuccess: (response) => {
            if (response.exceptions) {
              if (response.exceptions.length === 0) {
                const data = JSON.parse(response.data);
                const url = data.url;
                url && window.location.replace(url);
              }
            }
          },
        }
      );
    } catch(e) {
      console.warn(e)
    }
  }
  const loyaltyRepaymentHandler = () => {
    setOpenPaymentModal(false)
    try {
      redeemInit(
        {
          serviceFlowId: resolvedFlowId,
          redemptionType: '00009',
        },
        {
          onSuccess: async (response) => {
            validateResponse(response)
            if (response.exceptions) {
              if (response.exceptions.length === 0) {
                dispatch({ type: "SET_STEP", step: 6 });
                // dispatch({ type: "SET_SUMMARY", appointmentSummary: response });
                queryClient.invalidateQueries({ 
                  queryKey: [SERVICE_HISTORY, carInfo?.plateno]
                });
                setIsSummaryLoading(true)
                try{
                  const summaryRes = await getServiceBookingShowSummaryFn(resolvedFlowId)
                  validateResponse(summaryRes)
                  dispatch({ type: "SET_SUMMARY", appointmentSummary: summaryRes });
                } catch (e) {
                  console.warn(e)
                } finally {
                  setIsSummaryLoading(false)
                }
                const freshLoyaltyInfo = await getLoyaltyInfoFn();
                const { setLoyaltyInfo } = usePersonStore.getState();
                setLoyaltyInfo(freshLoyaltyInfo);
                setCurrentPaymentStatus("success");
              }
            }
          },
        }
      );
    } catch(e) {
      console.warn(e)
    }
  }

  const redirectToGoogleMaps = () => {
    let googleMapsUrl=""
    if(isPickup) {
      googleMapsUrl = `https://maps.google.com/?q=${state?.appointmentSummary?.pickupDeliveryAddress}`
    } else {
      googleMapsUrl = `https://www.google.com/maps/place/${state?.appointmentSummary?.bookingSelectObj?.[0]?.coordinates?.lat},${state?.appointmentSummary?.bookingSelectObj?.[0]?.coordinates?.lng}`
    }
    window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
  }

  const handleCancelAppointment = async () => {
    try {
      setIsCancelLoading(true)
      const response = await getServiceBookingCancelFn({serviceFlowId:resolvedFlowId, payload:{}});
      if (validateResponse(response)) {
        queryClient.invalidateQueries({
          queryKey: [SERVICE_HISTORY],
        });
        toast({
          variant: 'success',
          description: 'Το ραντεβού ακυρώθηκε',
        });
        setOpenModal(false)
        setIsCancelLoading(false)
        navigate("/service-booking");
        dispatch({ type: "RESET" });
        dispatch({ type: "SET_STEP", step: 0 });
      }
    } catch (e) {
      console.warn('Undo failed', e);
    } finally {
      setOpenModal(false)
      setIsCancelLoading(false)
    }
  }

  const summaryTitle = () => {
    if(afterViva && currentPaymentStatus === 'fail') {
      return (
      <>
        <h3>Το ραντεβού σου ΔΕΝ προγραμματίστηκε ακόμα...</h3>
        <p className='pt-3 text-sm text-center'>{`Η πληρωμή δεν έγινε επιτυχώς.`}</p>
        {state?.appointmentSummary?.pendingPayment && 
          <p className='pt-3 text-sm text-center'>Ξαναπροσπάθησε την πληρωμή σου ή κάλεσε στο 210 6879800 για να επικοινωνήσεις με έναν εκπρόσωπό μας.</p> 
        }
      </>
      )
    } else if((afterViva && currentPaymentStatus === 'success') || !afterViva) {
      const optionsSelection = 
        isCRMForm && isPickup ? "PickupCRM" : isCRMForm && !isPickup ? "CRM" : !isCRMForm && isPickup ? "PickupAvis" : "Avis"

      switch (optionsSelection) {
        case "PickupCRM":
          return (
            <>
              <CircledCheckIcon className="w-12 h-12 mb-3" />
               <h3 className='text-center'>Σύντομα θα λάβεις επιβεβαίωση για τον προγραμματισμό του ραντεβού σου.</h3>
              <p className='pt-3 text-sm text-center'>Ο οδηγός θα επικοινωνήσει τηλεφωνικά μαζί σου την ημέρα του ραντεβού για την ακριβή ώρα παραλαβής.</p> 
            </>
          )
        case "CRM":
          return (
            <>
              <CircledCheckIcon className="w-12 h-12 mb-3" />
              <h3 className='text-center'>Σύντομα θα λάβεις επιβεβαίωση για τον προγραμματισμό του ραντεβού σου.</h3>
              <p className='pt-3 text-sm text-center'>Θα λάβεις δύο υπενθυμίσεις την εβδομάδα πριν από το ραντεβού.</p> 
            </>
          )
        case "PickupAvis":
          return (
            <>
              <CircledCheckIcon className="w-12 h-12 mb-3" />
               <h3 className='text-center'>Το ραντεβού σου προγραμματίστηκε!</h3>
              <p className='pt-3 text-sm text-center'>Ο οδηγός θα επικοινωνήσει τηλεφωνικά μαζί σου την ημέρα του ραντεβού για την ακριβή ώρα παραλαβής.</p> 
            </>
          )
        case "Avis":
          return (
            <>
              <CircledCheckIcon className="w-12 h-12 mb-3" />
               <h3 className='text-center'>Το ραντεβού σου προγραμματίστηκε!</h3>
              <p className='pt-3 text-sm text-center'>Θα λάβεις δύο υπενθυμίσεις την εβδομάδα πριν από το ραντεβού.</p> 
            </>
          )
        default:
          return null
      }
    }
  }

  const summaryButtons = () => {
    return (
      <div className='flex flex-row gap-4 items-center justify-center pt-6 '>
        <button className='border border-fadedgrey rounded-md cursor-pointer' onClick={()=>{
          navigate(showRetailDashboard ? "/dashboard" : "/portal/my-cars");
        }}>
          <div className='flex flex-row gap-2 items-center justify-center py-3 px-10'>
            <ArrowLeftOutline className='w-4 h-4 stroke-black' />
            <p className='text-sm font-semibold'>My Cars</p>
          </div>
        </button>
        {(!afterViva || (afterViva && currentPaymentStatus === 'success')) && (!isCRMForm && state?.appointmentSummary?.bookingCalendar) && 
        <button className='bg-[#D4002A] rounded-md cursor-pointer' onClick={()=>{
          addToCalendar(state?.appointmentSummary)
        }}>
          <p className='text-sm text-white font-semibold py-3 px-10'>Προσθήκη στο ημερολόγιο</p>
        </button>}
        {state?.appointmentSummary?.pendingPayment && (state?.appointmentSummary?.paymentMethods.length > 1 ?
        <>
          <CustomModal
            triggerComponent={
              <div className='bg-[#D4002A] rounded-md cursor-pointer flex flex-row items-center justify-center py-3 px-10'>
                <p className='text-sm text-white font-semibold'>Πληρωμή ξανά</p>
              </div>
            }
            openModal={openPaymentModal}
            handleClick={(value) => {setOpenPaymentModal(value)}}
          >
            <div className="container-sm mx-auto pt-10 pb-10 px-7">
              <h3 className='w-full flex items-center justify-center pb-5'>Διαθέσιμοι τρόποι πληρωμής</h3>
              <div className='grid grid-cols-2 gap-3 max-lg:grid-cols-1'>
                {state?.appointmentSummary?.paymentMethods?.includes("viva") && <button className={`bg-white flex flex-col gap-2 w-full p-6 cursor-pointer rounded-xl border border-[#070707]`}
                onClick={()=>{
                  vivaRepaymentHandler()
                  setOpenPaymentModal(false)
                }}>
                  <PaymentCashIcon />
                  <div className='flex flex-col gap-2 items-start justify-start'>
                    <p className={`text-lg font-bold text-black text-left`}>Μετάβαση στη Viva</p>
                    <p className={`text-sm text-black text-left`}>Διαθέσιμες κάρτες χρεωστικές ή πιστωτικές, VISA, MasterCard</p>
                  </div>
                </button>}
                {state?.appointmentSummary?.paymentMethods?.includes("loyaltyPoints") && <button className={`bg-white flex flex-col gap-2 w-full p-6 cursor-pointer rounded-xl border border-[#070707]`}
                  onClick={()=>{
                    loyaltyRepaymentHandler()
                  }}>
                    <PaymentLoyaltyIcon />
                    <div className='flex flex-col gap-2 items-start justify-start'>
                      <p className={`text-lg font-bold text-black text-left`}>Εξαργύρωση χιλιομέτρων</p>
                      <p className={`text-sm text-black text-left`}>Διαθέσιμα προς εξαργύρωση {(loyaltyInfo?.awardPoints)?.toLocaleString("el-GR")} χιλιόμετρα</p>
                    </div>
                </button>}
              </div>
            </div>
          </CustomModal>
          {/* {state?.appointmentSummary?.paymentMethods.includes("viva") && <button className='bg-[#D4002A] rounded-md cursor-pointer' onClick={vivaRepaymentHandler}>
            <div className='flex flex-row gap-2 items-center justify-center py-3 px-10'>
              <p className='text-sm text-white font-semibold'>Πληρωμή</p>
            </div>
          </button>}
          {(state?.appointmentSummary?.paymentMethods.includes("loyalty") || 
          state?.appointmentSummary?.paymentMethods.includes("points")) && 
          <button className='bg-[#D4002A] rounded-md cursor-pointer' onClick={loyaltyRepaymentHandler}>
            <div className='flex flex-row gap-2 items-center justify-center py-3 px-10'>
              <p className='text-sm text-white font-semibold'>Εξαργύρωση Πόντων</p>
            </div>
          </button>} */}
        </>: state?.appointmentSummary?.paymentMethods.length === 1 && <button className='bg-[#D4002A] rounded-md cursor-pointer' onClick={vivaRepaymentHandler}>
            <div className='flex flex-row gap-2 items-center justify-center py-3 px-10'>
              <p className='text-sm text-white font-semibold'>Πληρωμή</p>
            </div>
          </button>) 
        }
      </div>
    )
  }

  const placeInfo = () => {
    const isAuthorized = 
      Object.keys(state?.appointmentSummary)?.includes("preferredGarageObj") && 
      Object.keys(state?.appointmentSummary?.preferredGarageObj?.[0])?.includes("type") && 
      state?.appointmentSummary?.preferredGarageObj?.[0]?.type === "Authorized"

    const isProvince = 
      Object.keys(state?.appointmentSummary)?.includes("preferredGarageObj") && 
      Object.keys(state?.appointmentSummary?.preferredGarageObj?.[0])?.includes("type") && 
      state?.appointmentSummary?.preferredGarageObj?.[0]?.type === 'Province'

    if(isPickup) {
      return (
        <>
          <p className="text-sm mb-2 font-bold">Διεύθυνση παραλαβής/παράδοσης</p>
          <div className='flex flex-row gap-2 items-center '>
            <MapPinIcon className='w-4 h-4'/>
            <p className="text-sm">{state?.appointmentSummary?.pickupDeliveryAddress}</p>
          </div>
        </>
      )
    } else if (isCRMForm) {
      return (
        <div className='flex flex-col gap-2'>
          <p className="text-sm font-bold">{isAuthorized? "Eξουσιοδοτημένο συνεργείο εξυπηρέτησης" : isProvince ? "Συνεργαζόμενο συνεργείο εξυπηρέτησης" : "Συνεργείο εξυπηρέτησης Avis"}</p>
          {state?.appointmentSummary?.preferredGarageObj?.[0] && <p className="text-sm font-bold">{state?.appointmentSummary?.preferredGarageObj?.[0]?.garageName}</p>}
          <div className='flex flex-row gap-2 items-center '>
            <MapPinIcon className='w-4 h-4'/>
            <p className="text-sm">{state?.appointmentSummary?.preferredGarageObj?.[0]?.address}</p>
          </div>
          {state?.appointmentSummary?.preferredGarageObj?.[0]?.phones?.length > 0 && <div className='flex flex-row gap-2 items-center'>
            <PhoneIcon className='w-4 h-4'/>
            <p className="text-sm">{state?.appointmentSummary?.preferredGarageObj?.[0]?.phones?.[0] || state?.appointmentSummary?.preferredGarageObj?.[0]?.phones?.[0]}</p>
          </div>}
          {(state?.appointmentSummary?.preferredGarageObj?.[0]?.coordinates || ["PickupCRMForm","PickupAvis"].includes(state?.appointmentSummary?.optionSelection)) && <button 
            disabled={!["PickupCRMForm","PickupAvis"].includes(state?.appointmentSummary?.optionSelection) && !state?.appointmentSummary?.preferredGarageObj?.[0]?.coordinates} 
            className='bg-none flex flex-row gap-2 items-center mt-2' 
            onClick={redirectToGoogleMaps}>
              <TrifoldMapIcon className={`w-4 h-4 ${(!["PickupCRMForm","PickupAvis"].includes(state?.appointmentSummary?.optionSelection) && !state?.appointmentSummary?.preferredGarageObj?.[0]?.coordinates) ? "text-fadedgrey" : "text-primary"}`} />
              <p className={`text-sm ${(!["PickupCRMForm","PickupAvis"].includes(state?.appointmentSummary?.optionSelection) && !state?.appointmentSummary?.preferredGarageObj?.[0]?.coordinates) ? "text-fadedgrey" : "text-primary"} font-semibold`}>Δες το στον χάρτη</p>
          </button>}
        </div>
      )
    } else {
      return (
        <div className='flex flex-col gap-2'>
          <p className="text-sm font-bold">{isAuthorized? "Eξουσιοδοτημένο συνεργείο εξυπηρέτησης" : isProvince ? "Συνεργαζόμενο συνεργείο εξυπηρέτησης" : "Συνεργείο εξυπηρέτησης Avis"}</p>
          {state?.appointmentSummary?.bookingSelectObj?.[0] && <p className="text-sm font-semibold">{state?.appointmentSummary?.bookingSelectObj?.[0]?.garageName}</p>}
          <div className='flex flex-row gap-2 items-center '>
            <MapPinIcon className='w-4 h-4'/>
            <p className="text-sm">{state?.appointmentSummary?.bookingSelectObj?.[0]?.address || state?.appointmentSummary?.bookingSelectObj}</p>
          </div>
          {state?.appointmentSummary?.bookingSelectObj?.[0]?.phones?.length > 0 && <div className='flex flex-row gap-2 items-center'>
            <PhoneIcon className='w-4 h-4'/>
            <p className="text-sm">{state?.appointmentSummary?.bookingSelectObj?.[0]?.phones?.[0] || state?.appointmentSummary?.bookingSelectObj?.[0]?.phones?.[0]}</p>
          </div>}
          {(state?.appointmentSummary?.bookingSelectObj?.[0]?.coordinates || ["PickupCRMForm","PickupAvis"].includes(state?.appointmentSummary?.optionSelection)) && <button 
            disabled={!["PickupCRMForm","PickupAvis"].includes(state?.appointmentSummary?.optionSelection) && !state?.appointmentSummary?.bookingSelectObj?.[0]?.coordinates} 
            className='bg-none flex flex-row gap-2 items-center mt-2' 
            onClick={redirectToGoogleMaps}>
              <TrifoldMapIcon className={`w-4 h-4 ${(!["PickupCRMForm","PickupAvis"].includes(state?.appointmentSummary?.optionSelection) && !state?.appointmentSummary?.bookingSelectObj?.[0]?.coordinates) ? "text-fadedgrey" : "text-primary"}`} />
              <p className={`text-sm ${(!["PickupCRMForm","PickupAvis"].includes(state?.appointmentSummary?.optionSelection) && !state?.appointmentSummary?.bookingSelectObj?.[0]?.coordinates) ? "text-fadedgrey" : "text-primary"} font-semibold`}>Δες το στον χάρτη</p>
          </button>}
        </div>
      )
    }
  }

    const timeInfo = () => {
    // if (isCRMForm) {
    //   const preferredTimeslots = state?.appointmentSummary?.preferredTimeslots
    //     ?.slice() // avoid mutating original state
    //     ?.sort((a, b) => new Date(a) - new Date(b)) // earliest → latest
    //     ?.map((slot) => formatBookingCalendar(slot));
    //   return (
    //     <div className='flex flex-col gap-2 pt-6'>
    //       <p className="text-sm font-bold">{"Επιλεγμένες ημέρες & ώρες"}</p>
    //         {preferredTimeslots?.map((slot) => {
    //           return <p className="text-sm">{slot?.day} {slot?.time}</p>
    //         })}
    //     </div>
    //   )
    // } else {
    //   const {day,time} = formatBookingCalendar(state?.appointmentSummary?.bookingCalendar)
    //   return (
    //     <>
    //       <p className="text-sm font-bold mb-2 pt-6">{"Επιλεγμένη ημέρα & ώρα"}</p>
    //       <p className="text-sm mb-2">{day}</p>
    //       <p className="text-sm">{time}</p>
    //     </>
    //   )
    // }
    const preferredTimeslots = state?.appointmentSummary?.bookingSlots
      ?.slice() // avoid mutating original state
      ?.sort((a, b) => new Date(a?.StartTime) - new Date(b?.StartTime)) // earliest → latest
      ?.map((slot) => {return {'StartTime':formatBookingCalendar(slot?.StartTime), 'EndTime':formatBookingCalendar(slot?.EndTime)}});
    return <div className='flex flex-col gap-2 pt-6'>
      <p className="text-sm font-bold">{isCRMForm ? "Επιλεγμένες ημέρες & ώρες" : "Επιλεγμένη ημέρα & ώρα"}</p>
        {preferredTimeslots?.map((slot) => {
          return <p className="text-sm">{slot?.StartTime?.day} {slot?.StartTime?.time} {`${isCRMForm || isPickup ? `- ${slot?.EndTime?.time}` : ""}`}</p>
        })}
    </div>
  }

  const cancelText = () => {
    const {day,time} = formatBookingCalendar(state?.appointmentSummary?.bookingCalendar)
    const works = state?.appointmentSummary?.workSelection?.split(",").map(w => w.trim()) || [];

    const standarizedService= works.includes("standarizedService")
    const tyreChange= works.includes("tyreChange")
    const bodyWork= works.includes("bodyWork")

    const work = 
      (standarizedService && !tyreChange && !bodyWork && "service") || 
      (!standarizedService && tyreChange && !bodyWork && "ελαστικών") || 
      (!standarizedService && !tyreChange && bodyWork && "φανοποιίας") || 
      (standarizedService && tyreChange && !bodyWork && "service - ελαστικών") || 
      (!standarizedService && tyreChange && bodyWork && "ελαστικών - φανοποιίας") || 
      (standarizedService && !tyreChange && bodyWork && "service - φανοποιίας") || 
      (standarizedService && tyreChange && bodyWork && "service - ελαστικών - φανοποιίας")

    return (<p>Θέλεις να ακυρώσεις το ραντεβού {work} {Object.keys(state?.appointmentSummary).includes("bookingCalendar") ? `για την ${day}` : ""} για το {carInfo?.plateno};</p>)
  }

  const paymentOptions = () => {
    return <>
      <div className='flex flex-row gap-3 max-lg:flex-col pt-5 '>
        <div className={`bg-white pt-5flex flex-col gap-2 w-full p-6 cursor-pointer rounded-xl ${!openPaymentModal ? "border outline outline-2 outline-[#070707] border-[#070707]" : "border-[1px] border-fadedgrey"}`}
          onClick={()=> {
            setOpenPaymentModal(false)
          }
        }>
          <PaymentCashIcon />
          <p className={`text-lg font-bold`}>Πληρωμή με κάρτα {state?.pickupCost}€</p>
          <p className={`text-sm`}>Διαθέσιμες κάρτες χρεωστικές ή πιστωτικές, VISA, MasterCard</p>
        </div>
        {<div className={`bg-white flex flex-col gap-2 w-full p-6 cursor-pointer rounded-xl ${openPaymentModal ? "border outline outline-2 outline-[#070707] border-[#070707]" : " border-[1px] border-fadedgrey"}`}
          onClick={()=> {
            setOpenPaymentModal(true)
          }}>
            <PaymentLoyaltyIcon />
            <div className='flex flex-col gap-2'>
              <p className={`text-lg font-bold text-black`}>Εξαργύρωση {(state?.loyaltyCost)?.toLocaleString("el-GR")} χλμ</p>
              <p className={`text-sm text-black`}>Διαθέσιμα προς εξαργύρωση {(loyaltyInfo?.awardPoints)?.toLocaleString("el-GR")} χιλιόμετρα</p>
            </div>
        </div>}
      </div>
      <div className='w-full flex flex-row items-center justify-center my-4'>
        <button className='bg-[#D4002A] rounded-md cursor-pointer' onClick={()=>{}}>
          <div className='flex flex-row gap-2 items-center justify-center py-3 px-10'>
            <p className='text-sm text-white font-semibold'>Πληρωμή</p>
          </div>
        </button>
      </div>
      <div className="h-[1px] w-full bg-fadedgrey" />
    </>
  }

  return (
    <>
      {!state?.appointmentSummary || isRedeemLoading || isSummaryLoading? <ServiceSummarySkeletion /> : <div className="flex flex-col w-full items-center justify-center mt-16" style={{ minHeight: "calc(100vh / 2)"}}>
        {summaryTitle()}
        {summaryButtons()}
        <div className='min-w-full md:min-w-[568px] min-h-[263px] m-8 p-6 border border-[#999999] rounded-xl bg-white'>
          {placeInfo()}
          {timeInfo()}
          {isPickup && 
          <>
            <p className="text-sm mt-6 font-bold">Pick-up & Delivery</p>
            <p className="text-sm pt-2">Ναι</p>
          </>}
          {!isCRMForm && <CustomModal
            triggerComponent={
              <p className="text-sm font-semibold text-[#D4002A] cursor-pointer pt-6">Ακύρωση ραντεβού</p>
            }
            openModal={openModal}
            handleClick={(value) => {setOpenModal(value)}}
          >
            <div className="container-sm mx-auto py-10">
              <ConfirmationDialog
                message={cancelText()}
                title="Ακύρωση προγραμματισμένου ραντεβού"
                isLoading = {isCancelLoading}
                
                handleOkClick={()=>handleCancelAppointment()}
                handleCancelClick={() => setOpenModal(false)}
                confirmCtaText="Ακύρωση"
                cancelCtaText="Επιστροφή"
              />
            </div>
          </CustomModal>}
        </div>
      </div>}
    </>
  );
};
